import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Database } from '@/integrations/supabase/types';
import { toast } from '@/hooks/use-toast';
import { actsAsLegacyRole, hasWorkRole, type WorkRole } from '@/lib/roles/multiRole';
import { ShopOperabilityGate } from '@/components/auth/ShopOperabilityGate';

type AppRole = Database['public']['Enums']['app_role'];

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: AppRole;
  requiredRoles?: AppRole[];
  /**
   * WORK ROLES THAT MAY ALSO ENTER, stated explicitly.
   *
   * `requiredRoles` speaks the legacy vocabulary, and a DETAIL_SPECIALIST has
   * no word in it: `legacyScalarFor` writes the neutral STAFF for them, and
   * `actsAsLegacyRole` deliberately refuses to project them onto TECHNICIAN so
   * that code predating work roles can never hand a detailer mechanical work.
   *
   * The consequence was that a perfectly valid detail-only employee — real
   * membership, real worker identity, real detail assignments — was answered
   * 403 at every route in the product, including the board that renders their
   * own cards. The new work role was unusable as a standalone job.
   *
   * This is the way in that does NOT lie about who they are: a route names the
   * work roles it is for, alongside the legacy roles it is for. Access is the
   * union; neither vocabulary is translated into the other.
   */
  requiredWorkRoles?: WorkRole[];
  passportAllowed?: boolean;
  /**
   * WS-GAPS2 B-W11: where a member this route REFUSES is sent instead of the
   * 403 page. It admits nobody here — the destination applies its own gate
   * (for `/` that is `/schedule-conflicts`, gated by the server predicate
   * `is_schedule_conflict_viewer`, which answers 403 to anyone it does not admit).
   */
  forbiddenRedirectTo?: string;
}

const ProtectedRoute = ({ children, requiredRole, requiredRoles, requiredWorkRoles, passportAllowed = false, forbiddenRedirectTo }: ProtectedRouteProps) => {
  const { user, membership, isLoading, userShops, isShopDataLoading, activeShop, isPassportMode, hasSchoolMembership, workRolesResolved } = useAuth();

  if (import.meta.env.DEV) {
    console.log('[ProtectedRoute] State:', { 
      hasUser: !!user, 
      isLoading, 
      isShopDataLoading,
      shopsCount: userShops?.length || 0,
      hasMembership: !!membership 
    });
  }

  // Routing helpers and debounced onboarding redirect to prevent flapping
  const navigate = useNavigate();
  const location = useLocation();
  const isOnOnboardPage = location.pathname.startsWith('/onboard');
  const onboardDebounceRef = useRef<number | null>(null);

  useEffect(() => {
    if (!user || isLoading || isShopDataLoading || isOnOnboardPage || isPassportMode) return;

    const noShops = (userShops?.length || 0) === 0;
    const noMembership = !membership;

    if (noShops && noMembership) {
      if (onboardDebounceRef.current) window.clearTimeout(onboardDebounceRef.current);
      // Increased debounce to 1500ms to give more time for invitation acceptance flow
      onboardDebounceRef.current = window.setTimeout(() => {
        const stillNoShops = (userShops?.length || 0) === 0;
        const stillNoMembership = !membership;
        if (stillNoShops && stillNoMembership) {
          if (import.meta.env.DEV) {
            console.log('[ProtectedRoute] Debounced redirect to /onboard');
          }
          navigate('/onboard', { replace: true });
        }
      }, 1500);
    } else {
      if (onboardDebounceRef.current) {
        window.clearTimeout(onboardDebounceRef.current);
        onboardDebounceRef.current = null;
      }
    }

    return () => {
      if (onboardDebounceRef.current) {
        window.clearTimeout(onboardDebounceRef.current);
        onboardDebounceRef.current = null;
      }
    };
  }, [user, isLoading, isShopDataLoading, userShops?.length, membership, isOnOnboardPage, navigate, isPassportMode]);

  // Show loading state while auth and shop data are resolving
  if (isLoading || isShopDataLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <div className="text-xl font-semibold">Loading...</div>
        </div>
      </div>
    );
  }

  // Redirect to auth if not authenticated
  if (!user) {
    if (import.meta.env.DEV) {
      console.log('[ProtectedRoute] No user, redirecting to /auth');
    }
    return <Navigate to="/auth" replace />;
  }

  // Wait for activeShop resolution when user has shops
  // Prevents components from firing queries with activeShop?.id before resolution
  if (userShops.length > 0 && !activeShop) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <div className="text-xl font-semibold">Loading shop...</div>
        </div>
      </div>
    );
  }

  // Passport mode: redirect to /passport if route is not passport-allowed.
  // FIX-R37-HIGH-3: surface a toast so passport users understand why a
  // shop-only route silently bounced them.
  // FIX-R38-CRIT-1: prefer /school if the user has a school membership.
  // (With the updated isPassportMode definition this branch is rarely hit
  // by school-only users, but the explicit reroute covers loading-window
  // edge cases and any future state that resets the school count.)
  if (isPassportMode && !passportAllowed) {
    if (import.meta.env.DEV) {
      console.log('[ProtectedRoute] Passport mode active, redirecting');
    }
    if (location.pathname !== '/passport' && location.pathname !== '/school') {
      toast({
        title: 'Shop required',
        description: 'This page needs an active shop membership. Showing your Passport instead.',
      });
    }
    return <Navigate to="/passport" replace />;
  }

  // FIX-R38-CRIT-1: a user with school membership but zero shop memberships
  // who hits a shop-required route should land on /school, not on the
  // generic 403 below. We treat school-only as a parallel context: shop
  // routes 403, but the implicit "go home" is /school for them.
  if (
    !isPassportMode
    && hasSchoolMembership
    && userShops.length === 0
    && !passportAllowed
    && location.pathname !== '/school'
    && !location.pathname.startsWith('/school/')
    && !location.pathname.startsWith('/passport')
  ) {
    if (import.meta.env.DEV) {
      console.log('[ProtectedRoute] School-only user hit a shop route, redirecting to /school');
    }
    toast({
      title: 'School portal',
      description: 'This page is shop-only. Showing your School portal instead.',
    });
    return <Navigate to="/school" replace />;
  }

  // Check role requirements (skip role checks for passport mode — no membership exists)
  //
  // TWO VOCABULARIES, ONE DECISION.
  //
  // The legacy half goes through `actsAsLegacyRole` rather than reading
  // `membership.role` directly — the same canonical projection `usePermissions`
  // uses, so a route and a permission can no longer disagree about who somebody
  // is. It answers identically for a legacy row and resolves authority and work
  // roles for a multi-role one.
  //
  // The work-role half is additive and explicit. A route that wants detail
  // workers says so; nothing converts a DETAIL_SPECIALIST into a TECHNICIAN to
  // get them in.
  const forbidden = (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="text-center">
        <h1 className="mb-4 text-4xl font-bold">403 - Forbidden</h1>
        <p className="text-xl text-muted-foreground">
          You don't have permission to access this page.
        </p>
      </div>
    </div>
  );

  // B-W11: a refused member may be sent to a route with its own gate instead of a dead end. Only a single-`/`
  // in-app path, never the route being refused (no loop).
  const refuse =
    forbiddenRedirectTo
    && forbiddenRedirectTo.startsWith('/') && !forbiddenRedirectTo.startsWith('//')
    && forbiddenRedirectTo !== location.pathname
      ? <Navigate to={forbiddenRedirectTo} replace />
      : forbidden;

  if (!isPassportMode) {
    const byWorkRole = (requiredWorkRoles ?? []).some((r) =>
      hasWorkRole(membership as never, r),
    );
    // OCT20A WS-BROWSER2: never REFUSE while the membership's work roles are still being read — before they arrive
    // the resolver sees only the legacy scalar, so a Detail-Specialist-only (or other work-role) member was refused
    // and, with `forbiddenRedirectTo`, permanently redirected away from `/` before their roles loaded. Wait instead;
    // the decision itself is unchanged.
    const refuseOrWait = workRolesResolved === false
      ? <div className="flex min-h-screen items-center justify-center bg-background"><div className="text-xl font-semibold">Loading...</div></div>
      : refuse;

    if (requiredRole && !byWorkRole && !actsAsLegacyRole(membership as never, requiredRole)) {
      return refuseOrWait;
    }

    if (requiredRoles && !byWorkRole && !actsAsLegacyRole(membership as never, ...requiredRoles)) {
      return refuseOrWait;
    }
  }

  // Core-100 R5 (CORE-U / OD-43): a shop founded but not yet set up is not operable — its members go to setup.
  // The server refuses its work regardless (shop_is_operable); Passport-allowed routes are not shop operations.
  if (!isPassportMode && !passportAllowed && activeShop?.id) {
    return <ShopOperabilityGate shopId={activeShop.id}>{children}</ShopOperabilityGate>;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
